/**
 * wireFormat.js — translation utilities between the v2 normalized schema
 * and the flat wire format used by the openHASP firmware JSON protocol.
 *
 * The firmware uses suffix-embedded property names (bg_color10, border_width20, …).
 * The v2 schema uses explicit part keys (parts.indicator.bg_color, …).
 * These functions bridge the two without touching internal node state.
 */

import { OPENHASP_PART_SUFFIX, COMMON_PROPS } from './shared.js'
import { STATE_DIGITS, CANONICAL_STATES } from './stateFormat.js'
import { objectSchemaV2 } from './index.js'

export { OPENHASP_PART_SUFFIX }

// ---------------------------------------------------------------------------
// Skip sets
// ---------------------------------------------------------------------------
// Part-level skip is now derived from schema metadata (meta.internal or
// meta.exportSpecial) rather than a hardcoded name list.
// OBJECT_PROP_SKIP remains explicit for top-level properties whose skip
// reason is about export routing, not about the metadata.

// Top-level object properties that belong to the editor/HA layer only
export const OBJECT_PROP_SKIP = new Set([
    // layout — exported separately as x/y/w/h
    'x', 'y', 'width', 'height', 'min_width', 'min_height',
    // identity — exported separately as id/parentid
    'haspid', 'parentid',
    // HA integration only
    'entityid',
    // designer stretch modes
    'stretch_mode',
    // designer-only preview toggle (dropdown open/closed simulation)
    'previewExpanded',
    // img internal path (firmware uses src, not filename)
    'filename',
    // handled with special formatting outside the main loop
    'action', 'command', 'groupid',
    // padding shorthand — expanded to pad_top/right/left/bottom outside the loop
    'padding',
    'bg_opa',
    'text_font_family',
])

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Return the v2 schema for an object type, or null if not yet defined.
 */
export function getObjectSchema(objType) {
    return objectSchemaV2[objType] ?? null
}

/**
 * Return the events declared by a Schema V2 widget.
 *
 * This is intentionally the only event-availability resolver used by the
 * editor surfaces. Event payload metadata stays on schema.events as well;
 * consumers that need it should read the matching definition from the schema.
 */
export function getAvailableEvents(schemaOrObjType) {
    return Object.keys(resolveObjectEvents(schemaOrObjType))
}

/**
 * Return the event map for an object type, schema or Designer node.
 *
 * A schema may declare `eventVariants: [{ when: { prop: value }, events }]`
 * when a property selects a different firmware event handler (btn.toggle,
 * arc.adjustable). Variants only apply when a node is passed, because only a
 * node carries property values; the first matching variant replaces
 * `schema.events`.
 */
export function resolveObjectEvents(source) {
    const { schema, isNode } = resolveSchemaSource(source)
    if (isNode) {
        const variant = (schema?.eventVariants ?? []).find(({ when }) =>
            Object.entries(when ?? {}).every(([property, value]) => source[property] === value))
        if (variant) return variant.events ?? {}
    }
    return schema?.events ?? {}
}

function resolveSchemaSource(source) {
    const isNode = typeof source === 'object' && source !== null &&
        (source.schemaV2 !== undefined || typeof source.obj === 'string')
    const schema = typeof source === 'string'
        ? getObjectSchema(source)
        : isNode ? source.schemaV2 ?? getObjectSchema(source.obj) : source
    return { schema, isNode }
}

/**
 * Return the gesture map (e.g. swipe directions) for an object type, schema
 * or node. Gestures run commands on the device but are not published events.
 */
export function resolveObjectGestures(source) {
    return resolveSchemaSource(source).schema?.gestures ?? {}
}

/** Return one event definition (payload metadata) for an object type, schema or node. */
export function getEventDefinition(source, event) {
    return resolveObjectEvents(source)[event] ?? null
}

/**
 * Return every registered widget object-type string. The schema registry
 * (objectSchemaV2) is the single source of truth for which widget types
 * exist — callers that need to enumerate them should use this instead of
 * maintaining their own list.
 */
export function getAllObjectTypes() {
    return Object.keys(objectSchemaV2)
}

/**
 * Return true when a top-level property is marked { hasp: true } in the schema,
 * meaning its runtime value is stored with the `hasp_` prefix on the node
 * (e.g. node.hasp_rotation, node.hasp_angle).
 */
export function isHaspProperty(schema, propName) {
    return schema?.properties?.[propName]?.hasp === true
}

/**
 * Compute the firmware wire key for a part + property name pair, optionally
 * in a non-default LVGL/OpenHASP state. The main part (suffix 0 / '') uses
 * the bare property name; all others append the part suffix. A non-default
 * state appends ITS suffix after the part suffix, matching real OpenHASP
 * wire data (e.g. indicator + checked → 'bg_color' + '10' + '01' = 'bg_color1001').
 *
 * When the part declares its own `exportSuffix` (see shared.js part metadata),
 * that value is authoritative. Otherwise falls back to the legacy LVGL_PARTS
 * lookup by part name, for schemas not yet migrated to part metadata.
 *
 * A property may instead declare its OWN `wireKey: '<firmware_attribute_name>'`
 * metadata (alongside its `type`/`label`/etc in the schema) — when present,
 * that literal string replaces the part-suffix-derived base outright, before
 * any state suffix is appended. This is for properties that live in a part
 * for Property Editor grouping/UX reasons but are genuinely flat, unsuffixed
 * firmware attributes that don't follow that part's numeric convention (e.g.
 * chart.js's `x_axis.x_scale` → ATTR_X_SCALE, always sent as bare `x_scale`
 * regardless of which part the schema nests it under). Unlike `exportSpecial`
 * (which removes a property from the generic export loop entirely, for cases
 * that change SHAPE — e.g. padding expanding into pad_top/right/left/bottom —
 * see ObjectExport.js), `wireKey` is a pure 1:1 rename: the property still
 * flows through the normal single-value export/import path, just under a
 * different literal key. Every consumer of wire keys (export, import,
 * config-merge cleanup, hasProperty enumeration, schema validation) computes
 * its key via this function, so declaring `wireKey` here is sufficient —
 * no other file needs to know about the override.
 *
 * PART AND STATE ARE SUMMED, NOT CONCATENATED. The firmware reads the trailing
 * digits as one two-digit number and splits it arithmetically —
 * `state = index % 10; part = index - state` in hasp_attribute_get_part_state_new()
 * — and it only takes that path for *exactly two* trailing digits; three or more
 * fall through to a legacy parser that honours only the last digit. So the
 * indicator part (10) in the checked state (1) is `bg_color11`, and a
 * concatenated `bg_color1001` would be silently misread as indicator/default.
 * The sum is zero-padded to two digits so that main + checked is `bg_color01`
 * rather than a single-digit `bg_color1`, which would also miss the new parser.
 *
 * @param partDef optional part definition (schema.parts[partName]) carrying
 *   exportSuffix AND properties (so `partDef.properties[propName].wireKey` can
 *   be checked)
 * @param state semantic state name (default: 'default' — digit 0)
 * @example toWireKey('indicator', 'bg_color') → 'bg_color10'
 * @example toWireKey('main',      'radius')   → 'radius'
 * @example toWireKey('selected',  'bg_color', { exportSuffix: '50' }) → 'bg_color50'
 * @example toWireKey('main',      'bg_color', partDef, 'checked') → 'bg_color01'
 * @example toWireKey('indicator', 'bg_color', partDef, 'checked') → 'bg_color11'
 * @example toWireKey('knob',      'outline_width', partDef, 'focused') → 'outline_width26'
 * @example toWireKey('x_axis', 'x_scale', { exportSuffix: '100', properties: { x_scale: { wireKey: 'x_scale' } } }) → 'x_scale'
 */
export function toWireKey(partName, propName, partDef, state = 'default') {
    const override = partDef?.properties?.[propName]?.wireKey
    const stateDigit = STATE_DIGITS[state] ?? 0

    // A wireKey override replaces the part-suffix arithmetic outright — it names
    // a flat firmware attribute with no part convention — but a state still has
    // to be distinguishable, so the state digit is appended to it as before.
    if (override) {
        return stateDigit === 0 ? override : `${override}${String(stateDigit).padStart(2, '0')}`
    }

    const partSuffix = Number(partDef?.exportSuffix ?? OPENHASP_PART_SUFFIX[partName] ?? 0)
    const suffix = partSuffix + stateDigit
    return suffix === 0 ? propName : `${propName}${String(suffix).padStart(2, '0')}`
}

/**
 * Parse a firmware wire key back to its part name, base property name and
 * semantic state — the exact mirror of toWireKey.
 *
 * The trailing two digits are one number, split arithmetically the way the
 * firmware does it: the units digit is the state, the tens digit the part. So
 * `bg_color11` is the indicator part in the checked state, and `bg_color06` is
 * the main part in the focused state. What is left after removing those two
 * digits is the property name. Like the part-suffix handling this replaces, it
 * assumes no real property name ends in digits.
 *
 * LEGACY: keys written by an older Designer concatenated the two suffixes
 * instead of summing them (`bg_color1001` for indicator+checked). Those never
 * worked on the firmware — it reads only the last digit of a 3+ digit suffix —
 * but they do exist in saved projects, so a four-digit key is still parsed the
 * old way on the way in. Nothing emits that form any more.
 *
 * @example fromWireKey('bg_color10')   → { partName: 'indicator', propName: 'bg_color', state: 'default' }
 * @example fromWireKey('val')          → { partName: 'main', propName: 'val', state: 'default' }
 * @example fromWireKey('bg_color01')   → { partName: 'main', propName: 'bg_color', state: 'checked' }
 * @example fromWireKey('bg_color11')   → { partName: 'indicator', propName: 'bg_color', state: 'checked' }
 * @example fromWireKey('bg_color06')   → { partName: 'main', propName: 'bg_color', state: 'focused' }
 * @example fromWireKey('bg_color1001') → { partName: 'indicator', propName: 'bg_color', state: 'checked' } // legacy
 *
 * @param schema optional — when given, a part's own declared `exportSuffix`
 *   (schema.parts[partName].exportSuffix) is checked FIRST, before the
 *   generic OPENHASP_PART_SUFFIX-by-name fallback. This matters for widgets
 *   whose part has a custom name sharing a generic suffix (e.g. checkbox's
 *   `bullet` part uses exportSuffix '10', the same digits OPENHASP_PART_SUFFIX
 *   maps to 'indicator' by name) — without the schema, the generic fallback
 *   would misidentify `bullet`'s wire keys as belonging to `indicator`.
 *
 *   Also when given, every part's properties are checked FIRST of all for a
 *   matching `wireKey` override (see toWireKey above) — an exact-string
 *   match, not suffix arithmetic, since an override property's wire key
 *   doesn't necessarily end in anything resembling a part/state suffix.
 *   Keeps this function the exact mirror of toWireKey's override handling,
 *   even though today's only caller passing real theme data (the legacy
 *   per-state theme migration path in StyleResolver.js) never exercises a
 *   schema with both states and a wireKey override.
 * @example fromWireKey('x_scale', chartSchema) → { partName: 'x_axis', propName: 'x_scale', state: 'default' }
 */
const STATE_NAME_BY_DIGIT = Object.fromEntries(CANONICAL_STATES.map(name => [STATE_DIGITS[name], name]))

/** Split the trailing digit group of a wire key into its state and the part
 *  suffix that precedes the property name. Returns null when there is none. */
function splitWireSuffix(wireKey) {
    const match = /^(.*?)(\d+)$/.exec(wireKey)
    if (!match) return null

    const [, base, digits] = match
    if (!base) return null // an all-digit key is not a property name

    // Legacy concatenated form: <part 2 digits><state 2 digits>
    if (digits.length === 4) {
        const state = STATE_NAME_BY_DIGIT[Number(digits.slice(2)) % 10]
        return { base, partSuffix: digits.slice(0, 2), state: state ?? 'default' }
    }

    if (digits.length !== 2) return null // 1 or 3+ digits: not a suffix this format produces

    const index = Number(digits)
    const stateDigit = index % 10
    const partSuffix = index - stateDigit
    return {
        base,
        partSuffix: partSuffix === 0 ? '' : String(partSuffix),
        state: STATE_NAME_BY_DIGIT[stateDigit] ?? 'default',
    }
}

export function fromWireKey(wireKey, schema) {
    // An overridden wire key is an exact string, not suffix arithmetic, so it has
    // to be matched before anything is stripped off the end.
    if (schema?.parts) {
        for (const [partName, partDef] of Object.entries(schema.parts)) {
            for (const [propName, meta] of Object.entries(partDef?.properties ?? {})) {
                if (meta?.wireKey && meta.wireKey === wireKey) {
                    return { partName, propName, state: 'default' }
                }
            }
        }
    }

    const split = splitWireSuffix(wireKey)
    if (!split) return { partName: 'main', propName: wireKey, state: 'default' }

    const { base: remainder, partSuffix, state } = split

    if (!partSuffix) return { partName: 'main', propName: remainder, state }

    // A part's own declared exportSuffix wins over the generic by-name table:
    // checkbox's `bullet` part uses '10', the same digits the generic table maps
    // to 'indicator', and without the schema its keys would land on the wrong part.
    if (schema?.parts) {
        for (const [partName, partDef] of Object.entries(schema.parts)) {
            if (partDef?.exportSuffix && String(partDef.exportSuffix) === partSuffix) {
                return { partName, propName: remainder, state }
            }
        }
    }

    for (const [partName, suffix] of Object.entries(OPENHASP_PART_SUFFIX)) {
        if (suffix === 0) continue
        if (String(suffix) === partSuffix) return { partName, propName: remainder, state }
    }

    return { partName: 'main', propName: remainder, state }
}

/**
 * Return all property keys that are valid for this object type — used to back
 * the `hasProperty` check on the node. Includes part wire-keys and top-level
 * properties; excludes only identity fields never checked via hasProperty.
 *
 * Unlike getExportableWireKeys, this set includes editor-only properties
 * (stretch_mode, keep_circle, padding, …) that the UI still guards with
 * hasProperty().
 */
export function getAllSchemaPropertyKeys(schema) {
    const keys = new Set()

    // All part properties → wire keys (keep_circle, padding etc. included
    // because the UI guards them with hasProperty)
    for (const [partName, partDef] of Object.entries(schema.parts)) {
        for (const propName of Object.keys(partDef.properties)) {
            keys.add(toWireKey(partName, propName, partDef))
        }
    }

    for (const propName of getTopLevelPropertyKeys(schema)) keys.add(propName)

    return keys
}

/**
 * Return the schema's top-level (non-style, non-part) property names —
 * COMMON_PROPS merged with schema.properties, excluding pure identity fields
 * that are never checked via hasProperty (they are accessed directly on the
 * node). Unlike getAllSchemaPropertyKeys, this never includes part-derived
 * wire keys — used by ConfigManager to copy config values onto a node
 * without touching part-scoped style data (that flows through node.parts).
 */
export function getTopLevelPropertyKeys(schema) {
    const IDENTITY_SKIP = new Set(['haspid', 'parentid'])
    const merged = { ...COMMON_PROPS, ...schema.properties }
    return Object.keys(merged).filter(propName => !IDENTITY_SKIP.has(propName))
}

// ---------------------------------------------------------------------------
// Wire-format ↔ normalized-parts translation
// ---------------------------------------------------------------------------

/**
 * Parse a flat firmware JSON object (keyed by wire names like 'bg_color10')
 * into the normalized parts structure used internally by the node.
 *
 * Returns { [partName]: { [propName]: value } } — suitable for node.parts.
 * Properties not present in the flat config are omitted (not set to undefined).
 */
export function fromWireFormat(schema, flatConfig) {
    const parts = {};
    for (const [partName, partDef] of Object.entries(schema.parts)) {
        parts[partName] = {};
        for (const propName of Object.keys(partDef.properties)) {
            const wk = toWireKey(partName, propName, partDef);
            if (flatConfig[wk] !== undefined) {
                parts[partName][propName] = flatConfig[wk];
            }
        }
    }
    return parts;
}

/**
 * Parse a flat firmware JSON object into the FULL normalized parts+states
 * structure (every semantic state the schema declares, not just 'default').
 * Returns { [partName]: { states: { [stateName]: { properties: { [propName]: value } } } } }
 * — directly assignable to node.parts (see ConfigManager.setNodeProperties).
 *
 * Sparse by construction: a state bucket is only created when at least one
 * of its wire keys is actually present in flatConfig ('default' is always
 * created, even empty, since every part always has a default state). This
 * is the single import-time translation step that replaces numeric-suffix
 * parsing everywhere else in the codebase — no other module may parse a
 * combined part+state wire key itself (see stateFormat.js).
 */
export function fromWireFormatStates(schema, flatConfig) {
    const parts = {};
    const supportedStates = ['default', ...(schema.states ?? [])];
    for (const [partName, partDef] of Object.entries(schema.parts)) {
        const statesOut = {};
        for (const state of supportedStates) {
            const properties = {};
            for (const propName of Object.keys(partDef.properties)) {
                const wk = toWireKey(partName, propName, partDef, state);
                if (flatConfig[wk] !== undefined) properties[propName] = flatConfig[wk];
            }
            if (state === 'default' || Object.keys(properties).length > 0) {
                statesOut[state] = { properties };
            }
        }
        parts[partName] = { states: statesOut };
    }
    return parts;
}

/**
 * Every combined part+state wire key a schema can produce for a given part —
 * used at import time to strip ALL raw wire-keyed attributes (default AND
 * state-suffixed) off the flat config before Konva's generic setAttrs(config)
 * would otherwise install them as stray flat attrs on the node.
 */
export function getAllWireKeysForPart(schema, partName, partDef) {
    const keys = [];
    for (const state of ['default', ...(schema.states ?? [])]) {
        for (const propName of Object.keys(partDef.properties ?? {})) {
            keys.push(toWireKey(partName, propName, partDef, state));
        }
    }
    return keys;
}

/**
 * Flatten a node's normalized parts structure back to a wire-format map.
 * Only includes exportable properties (metadata internal/exportSpecial flags and OBJECT_PROP_SKIP applied).
 * Does NOT apply notDefault filtering — that stays in ObjectExport.
 *
 * The returned map has wire keys as keys (e.g. 'bg_color10') and current
 * node values as values, ready to be compared against defaults and emitted.
 */
export function toWireFormat(schema, node) {
    const result = {};

    // Part-scoped style properties
    for (const [partName, partDef] of Object.entries(schema.parts)) {
        for (const [propName, meta] of Object.entries(partDef.properties)) {
            if (meta?.internal || meta?.exportSpecial) continue;
            const wk = toWireKey(partName, propName, partDef);
            // Resolved, not a direct node.parts read — stays correct once a
            // future cascade layer (theme/shared style) can contribute a
            // value that isn't materialized into node.parts. See
            // v2/StyleResolver.js.
            const value = node.styleProp(partName, propName);
            if (value !== undefined) result[wk] = value;
        }
    }

    // Top-level object properties
    for (const propName of Object.keys(schema.properties)) {
        if (OBJECT_PROP_SKIP.has(propName)) continue;
        const value = node[propName];
        if (value !== undefined) result[propName] = value;
    }

    return result;
}

/**
 * Return all firmware wire keys that should be considered for export for a
 * given v2 schema. Editor-only and specially-handled properties are excluded.
 *
 * The result is a flat string array of wire-format keys (e.g. 'bg_color10'),
 * ready to be iterated by the export loop in ObjectExport.js.
 */
export function getExportableWireKeys(schema) {
    const keys = []

    // Part-scoped style properties — skip if metadata marks them internal
    // (never in firmware JSON) or exportSpecial (handled outside the main loop).
    for (const [partName, partDef] of Object.entries(schema.parts)) {
        for (const [propName, meta] of Object.entries(partDef.properties)) {
            if (meta?.internal || meta?.exportSpecial) continue
            keys.push(toWireKey(partName, propName, partDef))
        }
    }

    // Top-level object properties (value, behavior, state).
    // COMMON_PROPS are merged in so every object exports opacity/click/etc.
    // when they differ from firmware defaults.
    const mergedProps = { ...COMMON_PROPS, ...schema.properties }
    for (const propName of Object.keys(mergedProps)) {
        if (OBJECT_PROP_SKIP.has(propName)) continue
        keys.push(propName)
    }

    return keys
}
